// Canvas 2D renderer: a direct port of the single-file viewer's renderScene.
// Kept as the fallback when WebGL2 is unavailable and as the reference the
// WebGL renderer is checked against.
import type { CameraState, CurveGeometry } from '../types.ts';
import type { FrameParams, Renderer, RenderStats } from './renderer.ts';
import { perspFactor, quatToMat3 } from './camera.ts';
import type { OverlayLines } from './renderer.ts';
import { RampCache, colorAt, cssRgb } from '../gradient/ramp.ts';
import { segmentWindow } from './range.ts';

interface SceneOpts extends FrameParams {
  passFrom?: number;
  passTo?: number;
  skipBg?: boolean;
  widthScale?: number;   // export scale applied to overlay widths
  /** On-screen frames: reuse the rasterised tile layer while camera and overlays are unchanged. */
  cacheBehind?: boolean;
}

interface Refine {
  cv: HTMLCanvasElement;
  g: CanvasRenderingContext2D;
  i: number;
  slices: number;
  p: FrameParams;
  raf: number;
  onDone: () => void;
}

const SLICE_MS = 14;   // work budget per animation frame during refinement

/** The cached layer is only valid while it was drawn from these very arrays. */
function behindStillRefers(cache: { refs?: Float32Array[] }, behind: { coords: Float32Array }[]): boolean {
  const refs = cache.refs;
  return !!refs && refs.length === behind.length && refs.every((r, i) => r === behind[i].coords);
}

export class Canvas2dRenderer implements Renderer {
  readonly kind = 'canvas2d' as const;
  readonly canvas: HTMLCanvasElement;
  readonly budget = { settled: 900000, interact: 120000 };
  private ctx: CanvasRenderingContext2D;
  private geom: CurveGeometry | null = null;
  private dpr = 1;
  private ramp = new RampCache();
  private refine: Refine | null = null;
  private refineBuf: HTMLCanvasElement | null = null;
  /** Rasterised "behind" overlays (hundreds of thousands of tile edges) for the current camera. */
  private behind: { key: string; cv: HTMLCanvasElement; refs: Float32Array[] } | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('2D canvas unavailable');
    this.ctx = ctx;
  }

  setGeometry(g: CurveGeometry | null): void {
    this.cancelRefine();
    this.geom = g;
  }

  resize(cssW: number, cssH: number, dpr: number): void {
    this.dpr = dpr;
    this.canvas.width = Math.max(2, Math.floor(cssW * dpr));
    this.canvas.height = Math.max(2, Math.floor(cssH * dpr));
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  draw(p: FrameParams): RenderStats {
    this.cancelRefine();
    return this.renderScene(this.ctx, p.viewW, p.viewH, p.cam, { ...p, cacheBehind: true });
  }

  cancelRefine(): void {
    if (this.refine && this.refine.raf) cancelAnimationFrame(this.refine.raf);
    this.refine = null;
  }

  refineProgressive(p: FrameParams, slices: number, onDone: () => void): void {
    this.cancelRefine();
    let cv2 = this.refineBuf;
    try {
      if (!cv2) { cv2 = document.createElement('canvas'); this.refineBuf = cv2; }
      if (cv2.width !== this.canvas.width) cv2.width = this.canvas.width;
      if (cv2.height !== this.canvas.height) cv2.height = this.canvas.height;
    } catch { return; }
    const g2 = cv2.getContext('2d', { alpha: false });
    if (!g2) return;
    g2.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.refine = { cv: cv2, g: g2, i: 0, slices, p: { ...p, cacheBehind: true } as FrameParams, raf: 0, onDone };
    this.refine.raf = requestAnimationFrame(() => this.stepRefine());
  }

  private stepRefine(): void {
    const r = this.refine;
    if (!r) return;
    const started = performance.now();
    do {
      const from = r.i / r.slices;
      const to = (r.i + 1) / r.slices;
      this.renderScene(r.g, r.p.viewW, r.p.viewH, r.p.cam, { ...r.p, passFrom: from, passTo: to, skipBg: r.i > 0 });
      r.i++;
    } while (r.i < r.slices && performance.now() - started < SLICE_MS);

    // Show partial progress so the view visibly sharpens rather than stalling.
    try {
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      this.ctx.drawImage(r.cv, 0, 0);
    } catch { /* ignore */ }
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    if (r.i < r.slices) {
      r.raf = requestAnimationFrame(() => this.stepRefine());
    } else {
      this.refine = null;
      r.onDone();
    }
  }

  async renderRegion(p: FrameParams, rx: number, ry: number, rw: number, rh: number, scale: number): Promise<HTMLCanvasElement> {
    const W = Math.max(2, Math.round(rw * scale));
    const H = Math.max(2, Math.round(rh * scale));
    const off = document.createElement('canvas');
    off.width = W;
    off.height = H;
    const g = off.getContext('2d');
    if (!g) throw new Error('Could not create export canvas');
    const cam2: CameraState = {
      scale: p.cam.scale * scale,
      tx: (p.cam.tx - rx) * scale,
      ty: (p.cam.ty - ry) * scale,
      rot: p.cam.rot,
    };
    this.renderScene(g, W, H, cam2, { ...p, lineWidth: p.lineWidth * scale, widthScale: scale });
    return off;
  }

  dispose(): void {
    this.cancelRefine();
    this.geom = null;
  }

  // Renders into any 2D context. cam2 maps world -> pixels of that context.
  private renderScene(gIn: CanvasRenderingContext2D, w: number, h: number, cam2: CameraState, opts: SceneOpts): RenderStats {
    let g = gIn;
    const geom = this.geom;
    const passFrom = opts.passFrom || 0;
    const passTo = opts.passTo === undefined ? 1 : opts.passTo;
    if (!opts.skipBg) {
      if (opts.transparent) g.clearRect(0, 0, w, h);
      else { g.fillStyle = opts.bgHex || '#ffffff'; g.fillRect(0, 0, w, h); }
    }
    if (!geom) return { drawn: 0, total: 0, stride: 1 };

    const f32 = geom.coords;
    const D = geom.dims;
    const nPoints = geom.nPoints;
    const three = opts.is3d;
    const stride = Math.max(1, opts.stride | 0);

    g.lineJoin = 'round';
    g.lineCap = 'round';
    g.lineWidth = opts.lineWidth;

    // Points actually drawn: an arithmetic progression, always including the
    // last point. Computed inline so no per-frame index array is allocated.
    const mStrided = Math.floor((nPoints - 1) / stride) + 1;
    const tailIsEnd = (mStrided - 1) * stride === nPoints - 1;
    const m = tailIsEnd ? mStrided : mStrided + 1;
    const idxAt = (k: number) => (k < mStrided ? k * stride : nPoints - 1) * D;
    if (m < 2) return { drawn: m, total: nPoints, stride };

    const closed = geom.closed;

    // Portion of the curve to draw, in strided point indices [kFirst, kLast]
    // with fractional clipping of the two end segments. The closing segment
    // of a closed curve is handled separately and only when the range reaches 1.
    const win = segmentWindow(opts.range, nPoints - 1, stride, m - 1);
    if (win.count <= 0) return { drawn: 0, total: nPoints, stride };
    const kFirst = win.first, kLast = win.first + win.count;
    const clipLo = win.lo, clipHi = win.hi;
    const drawClosing = closed && opts.range.end >= 1;

    // Projection inlined into scalars to avoid per-vertex allocation.
    const sc = cam2.scale, tx0 = cam2.tx, ty0 = cam2.ty;
    const rm = quatToMat3(cam2.rot);
    const m0 = rm[0], m1 = rm[1], m3 = rm[3], m4 = rm[4], m6 = rm[6], m7 = rm[7];
    // 3D rotation pivots on the bbox centre, like the WebGL renderer and fitToView.
    const bb = geom.bbox;
    const c0 = (bb.min[0] + bb.max[0]) / 2, c1 = (bb.min[1] + bb.max[1]) / 2, c2 = ((bb.min[2] ?? 0) + (bb.max[2] ?? 0)) / 2;
    const m2 = rm[2], m5 = rm[5], m8 = rm[8];
    const persp = three && opts.view3d.perspective, invExtent = 1 / Math.max(1e-9, opts.view3d.extent);
    const ax = opts.aspectK, ay = 1 / opts.aspectK;
    let sx = 0, sy = 0;
    const projectXYZ = (x: number, y: number, z: number) => {
      if (three) {
        const dx = x - c0, dy = y - c1, dz = z - c2;
        const f = persp ? perspFactor((m2 * dx + m5 * dy + m8 * dz) * invExtent) : 1;
        sx = (m0 * dx + m3 * dy + m6 * dz) * f * sc + tx0;
        sy = -(m1 * dx + m4 * dy + m7 * dz) * f * sc + ty0;
      } else {
        sx = x * ax * sc + tx0; sy = -y * ay * sc + ty0;
      }
    };
    const project = (j: number) => projectXYZ(f32[j], f32[j + 1], D > 2 ? f32[j + 2] : 0);
    const projectLerp = (ja: number, jb: number, f: number) => projectXYZ(
      f32[ja] + (f32[jb] - f32[ja]) * f,
      f32[ja + 1] + (f32[jb + 1] - f32[ja + 1]) * f,
      D > 2 ? f32[ja + 2] + (f32[jb + 2] - f32[ja + 2]) * f : 0,
    );
    // Strided point k, with the range's end points moved along their segments.
    const projectK = (k: number) => {
      if (k === kFirst && clipLo > 0) projectLerp(idxAt(k), idxAt(k + 1), clipLo);
      else if (k === kLast && clipHi < 1) projectLerp(idxAt(k - 1), idxAt(k), clipHi);
      else project(idxAt(k));
    };

    // Conservative viewport rejection: a segment is skipped only when its
    // bounding box misses the canvas entirely.
    const M = opts.lineWidth + 4;
    const vx0 = -M, vy0 = -M, vx1 = w + M, vy1 = h + M;
    const segVisible = (x0: number, y0: number, x1: number, y1: number) =>
      !((x0 < vx0 && x1 < vx0) || (x0 > vx1 && x1 > vx1) || (y0 < vy0 && y1 < vy0) || (y0 > vy1 && y1 > vy1));

    // Overlays (tile outlines, cage, endpoint dots): disjoint pairs in world coordinates.
    const widthScale = opts.widthScale ?? 1;
    const drawOverlay = (o: OverlayLines) => {
      const D = o.dims, n = o.coords.length / (2 * D);
      g.save();
      g.globalAlpha = o.alpha;
      g.strokeStyle = 'rgb(' + o.color[0] + ',' + o.color[1] + ',' + o.color[2] + ')';
      g.lineWidth = o.width * widthScale;
      g.lineCap = 'round';
      const cc = o.coords;
      for (let i0 = 0; i0 < n; i0 += 2048) {
        g.beginPath();
        for (let i = i0; i < Math.min(n, i0 + 2048); i++) {
          const a = i * 2 * D, b = a + D;
          projectXYZ(cc[a], cc[a + 1], D === 3 ? cc[a + 2] : 0);
          const x0 = sx, y0 = sy;
          projectXYZ(cc[b], cc[b + 1], D === 3 ? cc[b + 2] : 0);
          if (segVisible(x0, y0, sx, sy)) { g.moveTo(x0, y0); g.lineTo(sx, sy); }
        }
        g.stroke();
      }
      g.restore();
    };
    const behind = opts.overlays.filter((o) => o.width < 2), front = opts.overlays.filter((o) => o.width >= 2);
    if (!opts.skipBg && passFrom === 0 && behind.length) {
      if (opts.cacheBehind) {
        // Key on everything that moves the tile layer; overlay arrays are compared by identity.
        const key = [cam2.scale, cam2.tx, cam2.ty, cam2.rot.join(','), w, h, this.dpr, opts.aspectK, opts.is3d, opts.view3d.perspective, opts.view3d.extent,
          ...behind.map((o) => o.alpha + '/' + o.width + '/' + o.color.join('.') + '/' + o.coords.length + '/' + (o.coords as unknown as { __id?: number }).__id)].join('|');
        let cache = this.behind;
        if (!cache || cache.key !== key || !behindStillRefers(cache, behind)) {
          const cv = cache?.cv ?? document.createElement('canvas');
          if (cv.width !== this.canvas.width || cv.height !== this.canvas.height) { cv.width = this.canvas.width; cv.height = this.canvas.height; }
          const g2 = cv.getContext('2d')!;
          g2.setTransform(1, 0, 0, 1, 0, 0);
          g2.clearRect(0, 0, cv.width, cv.height);
          g2.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
          const saved = g; g = g2;
          for (const o of behind) drawOverlay(o);
          g = saved;
          cache = { key, cv, refs: behind.map((o) => o.coords) };
          this.behind = cache;
        }
        g.save(); g.setTransform(1, 0, 0, 1, 0, 0); g.drawImage(cache.cv, 0, 0); g.restore();
      } else {
        for (const o of behind) drawOverlay(o);
      }
    }
    g.globalAlpha = three ? opts.view3d.opacity : 1;

    if (opts.colorMode === 'solid') {
      g.strokeStyle = opts.solidHex || '#000000';
      const chunk = 50000;
      const kA = Math.max(kFirst, Math.floor(passFrom * (m - 1)));
      const kB = Math.min(kLast, Math.ceil(passTo * (m - 1)));
      if (kB <= kA) return { drawn: 0, total: nPoints, stride };
      g.beginPath();
      projectK(kA);
      let px = sx, py = sy, pen = false, since = 0;
      for (let k = kA + 1; k <= kB; k++) {
        projectK(k);
        if (segVisible(px, py, sx, sy)) {
          if (!pen) { g.moveTo(px, py); pen = true; }
          g.lineTo(sx, sy);
          if (++since % chunk === 0) { g.stroke(); g.beginPath(); g.moveTo(sx, sy); }
        } else {
          pen = false;
        }
        px = sx; py = sy;
      }
      if (drawClosing && passTo >= 1 && kB === m - 1) {
        project(0);
        if (segVisible(px, py, sx, sy)) { if (!pen) g.moveTo(px, py); g.lineTo(sx, sy); }
      }
      g.stroke();
    } else {
      const cyclic = opts.cyclic;
      const segs = m - 1;
      const blocks = Math.max(32, Math.min(256, Math.floor(m / 800)));
      const capSaved = g.lineCap;
      const bA = Math.max(0, Math.floor(passFrom * blocks));
      const bB = Math.min(blocks, Math.ceil(passTo * blocks));
      for (let b = bA; b < bB; b++) {
        // Block colours are fixed along the whole curve; the range only trims
        // which strided points of each block are stroked.
        const b0 = Math.floor((b * segs) / blocks);
        const b1 = Math.floor(((b + 1) * segs) / blocks);
        if (b1 <= b0) continue;
        const s0 = Math.max(b0, kFirst), s1 = Math.min(b1, kLast);
        if (s1 <= s0) continue;
        g.strokeStyle = cssRgb(colorAt(((b0 + b1) * 0.5) / Math.max(1, segs), cyclic, opts.grad, opts.colorMode, this.ramp));
        g.lineCap = s0 === kFirst || s1 === kLast ? 'round' : 'butt';
        g.beginPath();
        projectK(s0);
        let px = sx, py = sy, pen = false;
        for (let k = s0 + 1; k <= s1; k++) {
          projectK(k);
          if (segVisible(px, py, sx, sy)) {
            if (!pen) { g.moveTo(px, py); pen = true; }
            g.lineTo(sx, sy);
          } else {
            pen = false;
          }
          px = sx; py = sy;
        }
        g.stroke();
      }
      g.lineCap = capSaved;
      if (drawClosing && nPoints > 1 && passTo >= 1) {
        g.strokeStyle = cssRgb(colorAt(1, cyclic, opts.grad, opts.colorMode, this.ramp));
        g.beginPath();
        project((nPoints - 1) * D);
        const x1 = sx, y1 = sy;
        project(0);
        g.moveTo(x1, y1);
        g.lineTo(sx, sy);
        g.stroke();
      }
    }
    g.globalAlpha = 1;
    if (passTo >= 1) for (const o of front) drawOverlay(o);
    return { drawn: Math.min(m, win.count + 1), total: nPoints, stride };
  }
}
